import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { useCurrenciesStore } from '@/entities/currency';

import { VabsVirtualBalance } from '@/shared/api';

import {
  bankDetailsQuery,
  vabsAccountsQuery,
  vabsCurrenciesQuery,
} from '../api';
import { serializeAccount, serializeRequisites } from '../lib';

export const useAccountsHook = (enabled = false) => {
  const currencies = useCurrenciesStore((state) => state.currenciesById);

  return useInfiniteQuery({
    enabled,
    queryKey: ['vabs-accounts'],
    initialPageParam: 0,
    placeholderData: { pages: [], pageParams: [] },
    queryFn: async ({ pageParam }) => {
      const response = await vabsAccountsQuery(pageParam);
      const account = response.nodes[0];
      if (!account?.virtualBalances)
        return {
          accountId: null,
          count: 0,
          cursor: 0,
          nodes: [],
        };
      return {
        accountId: account.id,
        ...account.virtualBalances,
      };
    },
    getNextPageParam: ({ count, cursor }) =>
      cursor < count ? cursor : undefined,
    select: ({ pages }) =>
      pages.flatMap((page) =>
        page.nodes.map((node) =>
          serializeAccount(
            node as VabsVirtualBalance,
            page.accountId ?? '',
            node.currency?.id && currencies.get(node.currency.id),
          ),
        ),
      ),
  });
};

//this is currencies query with tag "for-open-account"
export const useVabsCurrenciesHook = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['vabs-currencies'],
    queryFn: vabsCurrenciesQuery,
  });

  return {
    data,
    isLoading,
  };
};

export const useRequisitesHook = (accountId: string, currencyIds: string[]) => {
  const { data: requisites, isLoading: isLoadingRequisites } = useQuery({
    queryKey: ['vabs-requisites', accountId, currencyIds],
    queryFn: () =>
      bankDetailsQuery({
        accId: accountId,
        currencyIds,
      }),
    select: (data) => serializeRequisites(data.bankDetails),
    enabled: Boolean(accountId),
  });
  return {
    data: requisites,
    isLoading: isLoadingRequisites,
  };
};
